<template>
  <div
    v-if="visible"
    :class="rootAnimationClass"
    element="InstagramFeedComponent"
    :data-hide-desktop="!internal_config.showDesktop"
    :data-hide-mobile="!internal_config.showMobile"
    :id="id"
    :style="style()"
  >
    <div class="instagram-feed" :style="feedWrapperStyle">
      <div v-if="loading && !posts.length" class="instagram-feed__loading">
        Loading…
      </div>

      <div v-else-if="connected" class="instagram-feed__content" :style="contentScrollStyle">
        <div
          v-if="internal_config.showHeader && profile"
          class="instagram-feed__header"
          :class="'instagram-feed__header--' + (internal_config.headerType || 'header1')"
          :style="headerStyle"
        >
          <div class="instagram-feed__profile">
            <img
              v-if="profile.profilePictureUrl"
              class="instagram-feed__avatar"
              :src="profile.profilePictureUrl"
              :alt="profile.username"
            />
            <div class="instagram-feed__avatar instagram-feed__avatar--placeholder" v-else></div>
            <div class="instagram-feed__meta">
              <div class="instagram-feed__username">@{{ profile.username }}</div>
              <div class="instagram-feed__stats">
                {{ formatCount(profile.mediaCount) }} posts · {{ formatCount(profile.followersCount) }} followers
              </div>
            </div>
          </div>
          <a
            class="instagram-feed__follow"
            :class="[
              { 'instagram-feed__follow--hoverable': internal_config.followButtonHoverEnabled },
              'instagram-feed__follow--icon-' + (internal_config.followButtonIconPosition || 'left'),
            ]"
            :href="profile.profileUrl"
            :target="linkTarget"
            rel="noopener noreferrer"
            :style="followButtonStyle"
          >
            <i v-if="followButtonIconClass" :class="followButtonIconClass"></i>
            <span>{{ followButtonLabel }}</span>
          </a>
        </div>

        <div v-if="!displayPosts.length" class="instagram-feed__empty-posts">No posts yet</div>

        <div v-else-if="feedLayout === 'single-featured'" class="instagram-feed__featured">
          <feed-post-tile
            :post="displayPosts[0]"
            :config="internal_config"
            tile-class="instagram-feed__tile--featured"
            :tile-style="featuredTileStyle"
            :engagement-style="engagementStyle"
            :likes-icon-style="likesIconStyle"
            :comments-icon-style="commentsIconStyle"
            @open="onPostClick"
          />
        </div>

        <div v-else-if="feedLayout === 'slider'" class="instagram-feed__slider-wrap">
          <button type="button" class="instagram-feed__slider-nav instagram-feed__slider-nav--prev" @click="slidePrev">‹</button>
          <div ref="sliderTrack" class="instagram-feed__slider">
            <feed-post-tile
              v-for="post in displayPosts"
              :key="post.id"
              :post="post"
              :config="internal_config"
              tile-class="instagram-feed__tile--slider"
              :tile-style="sliderTileStyle"
              :engagement-style="engagementStyle"
              :likes-icon-style="likesIconStyle"
              :comments-icon-style="commentsIconStyle"
              @open="onPostClick"
            />
          </div>
          <button type="button" class="instagram-feed__slider-nav instagram-feed__slider-nav--next" @click="slideNext">›</button>
        </div>

        <div v-else-if="feedLayout === 'masonry'" class="instagram-feed__masonry" :style="masonryStyle">
          <feed-post-tile
            v-for="(post, index) in displayPosts"
            :key="post.id"
            :post="post"
            :config="internal_config"
            tile-class="instagram-feed__tile--masonry"
            :tile-style="masonryTileStyle(index)"
            :engagement-style="engagementStyle"
            :likes-icon-style="likesIconStyle"
            :comments-icon-style="commentsIconStyle"
            @open="onPostClick"
          />
        </div>

        <div v-else class="instagram-feed__grid" :style="gridStyle">
          <feed-post-tile
            v-for="post in displayPosts"
            :key="post.id"
            :post="post"
            :config="internal_config"
            :tile-style="postTileStyle"
            :engagement-style="engagementStyle"
            :likes-icon-style="likesIconStyle"
            :comments-icon-style="commentsIconStyle"
            @open="onPostClick"
          />
        </div>

        <div v-if="internal_config.showLoadMore !== false && hasMore && feedLayout !== 'single-featured'" class="instagram-feed__footer">
          <button
            type="button"
            class="instagram-feed__load-more"
            :class="[
              { 'instagram-feed__load-more--hoverable': internal_config.loadMoreHoverEnabled },
              'instagram-feed__load-more--icon-' + (internal_config.loadMoreIconPosition || 'left'),
            ]"
            :disabled="loadingMore"
            :style="loadMoreButtonStyle"
            @click="loadMore"
          >
            <i v-if="loadMoreIconClass" :class="loadMoreIconClass"></i>
            <span>{{ loadingMore ? 'Loading…' : loadMoreLabel }}</span>
          </button>
        </div>
        <p v-if="internal_config.showBranding !== false" class="instagram-feed__branding">Instagram</p>
      </div>
    </div>
    <post-lightbox ref="postLightbox" />
  </div>
</template>

<script>
import Model from './Model';
import { getInstagramFeed } from '../../../js/services/instagram';
import { applyManualPostLayout, orderFeedPosts, resolveFeedLayout, resolveLayoutFamily } from './layoutPresets';
import PostLightbox from './PostLightbox.vue';
import FeedPostTile from './FeedPostTile.vue';

export default {
  name: 'InstagramFeedComponent',
  components: {
    PostLightbox,
    FeedPostTile,
  },
  props: ['config', 'height_start', 'device', 'id', 'preview'],
  data() {
    return {
      ...Model.data(),
      visible: true,
      connected: false,
      loading: false,
      loadingMore: false,
      profile: null,
      posts: [],
      nextCursor: null,
      hasMore: false,
      postOrderSeed: Math.floor(Math.random() * 2147483646) + 1,
      rootAnimationClass: 'sitebuilder-element-container',
    };
  },
  computed: {
    followButtonLabel() {
      const text = this.internal_config.followButtonText;
      return text && String(text).trim() ? String(text).trim() : 'Follow on Instagram';
    },
    loadMoreLabel() {
      const text = this.internal_config.loadMoreText;
      return text && String(text).trim() ? String(text).trim() : 'Load More Posts';
    },
    filteredPosts() {
      return this.filterPosts(this.posts);
    },
    feedLayout() {
      return resolveFeedLayout(this.internal_config.feedLayout);
    },
    linkTarget() {
      return this.internal_config.openLinksInNewTab === false ? '_self' : '_blank';
    },
    displayPosts() {
      const posts = applyManualPostLayout(
        orderFeedPosts(
          this.filteredPosts,
          this.internal_config.postOrder,
          this.postOrderSeed,
        ),
        this.internal_config.customPostOrder,
        this.internal_config.hiddenPostIds,
      );
      if (this.feedLayout === 'single-featured') {
        return posts.slice(0, 1);
      }
      return posts;
    },
    headerStyle() {
      return {
        minHeight: this.internal_config.feedHeaderHeight || '80px',
      };
    },
    followButtonStyle() {
      const c = this.internal_config;
      const textStyle = this.buildTextStyle(c.followButtonTextStyle, c.followButtonColor || '#FFFFFF');
      return Object.assign({}, textStyle, {
        background: this.buildButtonBackground(c, 'followButton'),
        width: c.followButtonWidth || 'auto',
        minHeight: c.followButtonHeight || '40px',
        borderRadius: c.followButtonBorderRadius || '8px',
        gap: '8px',
        textDecoration: 'none',
        '--ig-follow-hover-bg': c.followButtonHoverBackground || c.followButtonBackground || '#1877F2',
        '--ig-follow-hover-color': c.followButtonHoverColor || textStyle.color || '#FFFFFF',
      });
    },
    loadMoreButtonStyle() {
      const c = this.internal_config;
      const textStyle = this.buildTextStyle(c.loadMoreTextStyle, c.loadMoreColor || '#262626');
      return Object.assign({}, textStyle, {
        background: this.buildButtonBackground(c, 'loadMore'),
        width: c.loadMoreButtonWidth || '100%',
        maxWidth: c.loadMoreButtonWidth || '420px',
        minHeight: c.loadMoreButtonHeight || '44px',
        borderRadius: c.loadMoreBorderRadius || '8px',
        gap: '8px',
        '--ig-load-more-hover-bg': c.loadMoreHoverBackground || c.loadMoreBackground || '#E0E0E0',
        '--ig-load-more-hover-color': c.loadMoreHoverColor || textStyle.color || '#262626',
      });
    },
    followButtonIconClass() {
      const icon = this.internal_config.followButtonIcon;
      if (!icon) {
        return '';
      }
      return this.$addIconPrefix ? this.$addIconPrefix(icon) : icon;
    },
    loadMoreIconClass() {
      const icon = this.internal_config.loadMoreIcon;
      if (!icon) {
        return '';
      }
      return this.$addIconPrefix ? this.$addIconPrefix(icon) : icon;
    },
    postTileStyle() {
      const height = parseInt(this.internal_config.postHeight, 10);
      return {
        borderRadius: this.imageRadius,
        minHeight: Number.isNaN(height) ? undefined : `${height}px`,
        height: Number.isNaN(height) ? undefined : `${height}px`,
      };
    },
    featuredTileStyle() {
      const height = parseInt(this.internal_config.postHeight, 10);
      const featuredHeight = Number.isNaN(height) ? 420 : Math.max(height, 320);
      return {
        borderRadius: this.imageRadius,
        minHeight: `${featuredHeight}px`,
        height: `${featuredHeight}px`,
      };
    },
    sliderTileStyle() {
      const visible = this.sliderVisibleCount;
      const gap = parseInt(this.internal_config.gap, 10) || 12;
      const width = `calc((100% - ${(visible - 1) * gap}px) / ${visible})`;
      const height = parseInt(this.internal_config.postHeight, 10) || 180;
      return {
        borderRadius: this.imageRadius,
        flex: `0 0 ${width}`,
        maxWidth: width,
        minHeight: `${height}px`,
        height: `${height}px`,
      };
    },
    sliderVisibleCount() {
      if (resolveLayoutFamily(this.internal_config.feedLayout) === 'slider') {
        return parseInt(this.internal_config.columns, 10)
          || parseInt(this.internal_config.sliderVisibleCount, 10)
          || 4;
      }
      return parseInt(this.internal_config.sliderVisibleCount, 10) || 4;
    },
    masonryStyle() {
      const columns = this.layoutColumnCount;
      const gap = parseInt(this.internal_config.gap, 10) || 12;
      return {
        columnCount: columns,
        columnGap: `${gap}px`,
      };
    },
    engagementStyle() {
      return {
        fontSize: `${parseInt(this.internal_config.engagementIconSize, 10) || 14}px`,
      };
    },
    likesIconStyle() {
      return {
        color: this.internal_config.likesIconColor || '#FFFFFF',
      };
    },
    commentsIconStyle() {
      return {
        color: this.internal_config.commentsIconColor || '#FFFFFF',
      };
    },
    contentScrollStyle() {
      const height = parseInt(this.internal_config.height, 10);
      if (Number.isNaN(height) || height <= 0) {
        return {};
      }

      return {
        maxHeight: `${height}px`,
        overflowY: 'auto',
        overflowX: 'hidden',
      };
    },
    feedWrapperStyle() {
      const c = this.internal_config;
      return {
        width: '100%',
        maxWidth: c.maxWidth || '900px',
        margin: '0 auto',
        backgroundColor: c.backgroundColor || '#FFFFFF',
        backgroundImage: c.backgroundImage && c.backgroundImage !== 'none' ? `url('${c.backgroundImage}')` : 'none',
        backgroundPosition: c.backgroundPosition || 'center',
        backgroundRepeat: c.backgroundRepeat || 'no-repeat',
        backgroundSize: c.backgroundSize || 'cover',
        backgroundAttachment: c.backgroundAttachment || 'scroll',
        border: c.border || '0 none #000000',
        boxShadow: c.boxShadow || 'none',
        borderRadius: c.borderRadius || '0',
      };
    },
    gridStyle() {
      const columns = this.layoutColumnCount;
      const gap = parseInt(this.internal_config.gap, 10) || 12;
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: `${gap}px`,
      };
    },
    layoutColumnCount() {
      if (resolveLayoutFamily(this.internal_config.feedLayout) === 'list') {
        return 1;
      }
      return parseInt(this.internal_config.columns, 10) || 3;
    },
    imageRadius() {
      const radius = this.internal_config.imageBorderRadius;
      return typeof radius === 'number' ? `${radius}px` : (radius || '8px');
    },
  },
  mounted() {
    this.internal_config = window.mergeRecursive
      ? window.mergeRecursive(this.props, this.$props.config || {})
      : Object.assign({}, this.props, this.$props.config || {});
    this.loadFeed(true);
    this.$nextTick(() => {
      this.applyFeedAnimation();
    });
  },
  methods: {
    applyFeedAnimation() {
      if (!this.$el) {
        return;
      }
      const animation = this.internal_config.animation && this.internal_config.animation.in;
      if (!animation || !animation.effect || animation.effect === 'none') {
        return;
      }
      this.$root.$emit('applyAnimation', this, 'sitebuilder-element-container');
      this.syncAnimationClass();
      if (animation.action === 'time') {
        const delay = ((parseFloat(animation.time) || 0) * 1000) + 80;
        setTimeout(() => {
          this.syncAnimationClass();
        }, delay);
      }
    },
    syncAnimationClass() {
      if (this.$el && this.$el.className) {
        this.rootAnimationClass = this.$el.className;
      }
    },
    style() {
      const c = this.internal_config;
      return `
        left: ${c.left || '0'};
        top: ${c.top || '0'};
        z-index: ${c.zIndex || 'auto'};
        max-width: ${c.maxWidth || '900px'};
        width: ${c.width || '720px'};
        margin: 0 auto;
        padding-top: ${c.paddingTop || '20px'};
        padding-left: ${c.paddingLeft || '20px'};
        padding-right: ${c.paddingRight || '20px'};
        padding-bottom: ${c.paddingBottom || '20px'};
      `;
    },
    buildTextStyle(textStyle, fallbackColor) {
      const ts = textStyle || {};
      return {
        fontFamily: ts.family || 'Arial, Helvetica, sans-serif',
        fontSize: ts.size || '14px',
        color: ts.color || fallbackColor || '#FFFFFF',
        textAlign: ts.align || 'center',
        fontWeight: ts.weight || '600',
        fontStyle: ts.style || 'initial',
        textDecoration: ts.stroke || 'none',
      };
    },
    buildButtonBackground(config, prefix) {
      const colorKey = prefix === 'followButton' ? 'followButtonBackground' : 'loadMoreBackground';
      let bg = config[colorKey] || (prefix === 'followButton' ? '#0095F6' : '#EFEFEF');
      const imageKey = `${prefix}BackgroundImage`;
      if (config[imageKey] && config[imageKey] !== 'none') {
        bg += ` url("${config[imageKey]}")`;
        bg += ` ${config[`${prefix}BackgroundPosition`] || 'center'}`;
        bg += ` / ${config[`${prefix}BackgroundSize`] || 'cover'}`;
        bg += ` ${config[`${prefix}BackgroundRepeat`] || 'no-repeat'}`;
        bg += ` ${config[`${prefix}BackgroundOrigin`] || 'border-box'}`;
      }
      return bg;
    },
    masonryTileStyle(index) {
      const baseHeight = parseInt(this.internal_config.postHeight, 10) || 180;
      const tall = index % 3 === 0;
      const height = tall ? baseHeight + 100 : baseHeight;
      return {
        borderRadius: this.imageRadius,
        display: 'inline-block',
        width: '100%',
        marginBottom: `${parseInt(this.internal_config.gap, 10) || 12}px`,
        minHeight: `${height}px`,
        height: `${height}px`,
      };
    },
    openPostLightbox(post) {
      if (!post || !this.$refs.postLightbox) {
        return;
      }
      this.$refs.postLightbox.open(post, {
        imageSrc: this.postImage(post),
        showEngagement: this.internal_config.showEngagement !== false,
        showLikes: this.internal_config.showLikes !== false,
        showComments: this.internal_config.showComments !== false,
        engagementStyle: Object.assign({}, this.engagementStyle, { fontSize: '28px' }),
        likesIconStyle: this.likesIconStyle,
        commentsIconStyle: this.commentsIconStyle,
      });
    },
    onPostClick(post) {
      const isVideo = post && (post.mediaType === 'VIDEO' || post.mediaType === 'REELS');
      if (!isVideo && this.internal_config.openLinksInNewTab !== false && post && post.permalink) {
        window.open(post.permalink, '_blank', 'noopener,noreferrer');
        return;
      }
      this.openPostLightbox(post);
    },
    slidePrev() {
      const track = this.$refs.sliderTrack;
      if (!track) {
        return;
      }
      track.scrollBy({ left: -(track.clientWidth * 0.75), behavior: 'smooth' });
    },
    slideNext() {
      const track = this.$refs.sliderTrack;
      if (!track) {
        return;
      }
      track.scrollBy({ left: track.clientWidth * 0.75, behavior: 'smooth' });
    },
    filterPosts(posts) {
      const list = Array.isArray(posts) ? posts : [];
      const include = (this.internal_config.filterIncludeHashtags || [])
        .map((tag) => String(tag).replace(/^#/, '').toLowerCase())
        .filter(Boolean);
      const exclude = (this.internal_config.filterExcludeHashtags || [])
        .map((tag) => String(tag).replace(/^#/, '').toLowerCase())
        .filter(Boolean);

      return list.filter((post) => {
        const caption = String(post.caption || '').toLowerCase();
        if (exclude.length && exclude.some((tag) => caption.indexOf(`#${tag}`) !== -1 || caption.indexOf(tag) !== -1)) {
          return false;
        }
        if (include.length) {
          return include.some((tag) => caption.indexOf(`#${tag}`) !== -1 || caption.indexOf(tag) !== -1);
        }
        return true;
      });
    },
    formatCount(value) {
      const n = Number(value) || 0;
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      }
      if (n >= 1000) {
        return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      }
      return String(n);
    },
    postImage(post) {
      if (post.mediaType === 'VIDEO' || post.mediaType === 'REELS') {
        return post.thumbnailUrl || post.mediaUrl;
      }
      return post.mediaUrl || post.thumbnailUrl;
    },
    loadFeed(reset) {
      if (reset) {
        this.loading = true;
        this.posts = [];
        this.nextCursor = null;
      }
      const limit = parseInt(this.internal_config.postsPerPage, 10) || 9;
      return getInstagramFeed({
        limit,
        cursor: reset ? null : this.nextCursor,
      })
        .then((res) => {
          const data = (res.data && res.data.data) || res.data || {};
          this.connected = !!data.connected;
          this.visible = this.connected;
          this.profile = data.profile || null;
          const pagePosts = data.posts || [];
          this.posts = reset ? pagePosts : this.posts.concat(pagePosts);
          this.nextCursor = data.paging && data.paging.nextCursor ? data.paging.nextCursor : null;
          this.hasMore = !!(data.paging && data.paging.hasMore);
          this.loading = false;
          this.loadingMore = false;
        })
        .catch(() => {
          this.loading = false;
          this.loadingMore = false;
          this.connected = false;
          this.visible = false;
        });
    },
    loadMore() {
      if (!this.hasMore || this.loadingMore) {
        return;
      }
      this.loadingMore = true;
      this.loadFeed(false);
    },
  },
};
</script>

<style scoped>
.instagram-feed__loading {
  padding: 40px;
  text-align: center;
  color: #8e8e8e;
}
.instagram-feed__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.instagram-feed__header--header2 {
  flex-direction: column;
  align-items: flex-start;
}
.instagram-feed__profile {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.instagram-feed__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.instagram-feed__avatar--placeholder {
  background: #efefef;
}
.instagram-feed__username {
  font-weight: 700;
  font-size: 16px;
  color: #262626;
}
.instagram-feed__stats {
  font-size: 13px;
  color: #8e8e8e;
  margin-top: 2px;
}
.instagram-feed__follow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
}
.instagram-feed__follow--hoverable:hover {
  background: var(--ig-follow-hover-bg) !important;
  color: var(--ig-follow-hover-color) !important;
}
.instagram-feed__featured {
  width: 100%;
}
.instagram-feed__tile--featured {
  width: 100%;
}
.instagram-feed__slider-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.instagram-feed__slider {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex: 1;
}
.instagram-feed__slider::-webkit-scrollbar {
  display: none;
}
.instagram-feed__slider-nav {
  border: 0;
  background: #efefef;
  color: #262626;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.instagram-feed__masonry {
  width: 100%;
}
.instagram-feed__tile--masonry {
  break-inside: avoid;
}
.instagram-feed__tile--slider {
  position: relative;
  display: block;
  overflow: hidden;
  background: #f0f0f0;
  text-decoration: none;
  color: #fff;
}
.instagram-feed__follow--icon-right,
.instagram-feed__load-more--icon-right {
  flex-direction: row-reverse;
}
.instagram-feed__load-more--hoverable:hover {
  background: var(--ig-load-more-hover-bg) !important;
  color: var(--ig-load-more-hover-color) !important;
}
.instagram-feed__load-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.instagram-feed__tile {
  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: auto;
  background: #f0f0f0;
  text-decoration: none;
  color: #fff;
  border: 0;
  padding: 0;
  margin: 0;
  width: 100%;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}
.instagram-feed__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.instagram-feed__engagement {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  gap: 12px;
  padding: 8px 10px;
  font-weight: 600;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
}
.instagram-feed__footer {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
.instagram-feed__load-more {
  border: 0;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.instagram-feed__empty-posts {
  text-align: center;
  color: #8e8e8e;
  padding: 24px;
}
.instagram-feed__branding {
  margin: 12px 0 0;
  text-align: center;
  font-size: 12px;
  color: #8e8e8e;
}
</style>
